// src/audio/playback.ts — decode + play 24 kHz PCM (grounding output contract), plus an
// iOS/Safari unlock() that must be called from a user gesture (the PTT press).
// Ported from LLABARO.jsx playPcm24k.
import { LIVE_OUTPUT_HZ } from "../config";

let sharedCtx: AudioContext | null = null;
function ctx(): AudioContext {
  if (!sharedCtx) sharedCtx = new AudioContext();
  return sharedCtx;
}

export async function unlock(): Promise<void> {
  const c = ctx();
  if (c.state === "suspended") {
    try {
      await c.resume();
    } catch {
      /* ignore */
    }
  }
}

/** Play base64-encoded PCM 24 kHz/16-bit mono at the given volume. */
export async function playPcm24kBase64(b64: string, volume = 1): Promise<void> {
  const raw = atob(b64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  await playPcm24k(bytes.buffer, volume);
}

// Sequential playback cursor for streaming live audio (schedules chunks back-to-back).
let playCursor = 0;

/** Enqueue a PCM 24 kHz/16-bit mono chunk to play gaplessly after the previous one. */
export function enqueuePcm24k(pcm: ArrayBuffer, volume = 1): void {
  const c = ctx();
  const view = new DataView(pcm);
  const n = Math.floor(pcm.byteLength / 2);
  if (n === 0) return;
  const buf = c.createBuffer(1, n, LIVE_OUTPUT_HZ);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < n; i++) ch[i] = view.getInt16(i * 2, true) / 32768;
  const gain = c.createGain();
  gain.gain.value = Math.max(0, Math.min(1, volume));
  const src = c.createBufferSource();
  src.buffer = buf;
  src.connect(gain);
  gain.connect(c.destination);
  const startAt = Math.max(c.currentTime + 0.02, playCursor);
  src.start(startAt);
  playCursor = startAt + buf.duration;
}

/** Play raw PCM 24 kHz/16-bit mono (little-endian) at the given volume. */
export async function playPcm24k(pcm: ArrayBuffer, volume = 1): Promise<void> {
  const c = ctx();
  await unlock();
  const view = new DataView(pcm);
  const n = Math.floor(pcm.byteLength / 2);
  const buf = c.createBuffer(1, n, LIVE_OUTPUT_HZ);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < n; i++) ch[i] = view.getInt16(i * 2, true) / 32768;
  const gain = c.createGain();
  gain.gain.value = Math.max(0, Math.min(1, volume));
  const s = c.createBufferSource();
  s.buffer = buf;
  s.connect(gain);
  gain.connect(c.destination);
  s.start();
  await new Promise<void>((res) => {
    s.onended = () => res();
  });
}
